import { useMemo } from 'react'
import PropTypes from 'prop-types'

import { can, CAPABILITY } from '@aytm/app/stats/page/data-quality-report/lib/tiers'

// components
import Block from '@aytm/app/stats/page/data-centrifuge/Block'

// styles
import style from '@aytm/app/stats/page/data-centrifuge/styles/Component.module.scss'

const formatLastRunTs = ts => {
  if (!ts) return ''
  const event = new Date(ts)
  const date = event.toLocaleDateString('en-US')
  const time = event.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZoneName: 'short' })
  return `Last run: ${date} ${time}`
}

const Footer = ({ dcLastRunTs, dcAnalyzedCount, notAnalyzedCount }) => {
  const lastRunText = formatLastRunTs(dcLastRunTs)
  // The population is the run's own — what it analysed plus what it left unanalysed — so the
  // sentence's containment holds by construction. The page's response total is a different set
  // read at a different moment and can sit below the analysed figure.
  const countText = dcAnalyzedCount ? `${dcAnalyzedCount} of ${dcAnalyzedCount + notAnalyzedCount} analysed` : ''

  return 0 < lastRunText.length || 0 < countText.length ? (
    <div data-testid="stat-dc-footer" className={style.footer}>
      {0 < lastRunText.length ? <span>{lastRunText}</span> : null}
      {0 < countText.length ? <span>{countText}</span> : null}
    </div>
  ) : null
}

Footer.propTypes = {
  // dcLastRunTs arrives as an ISO timestamp string from the AYTMstats wire
  // payload (data_centrifuge_view writes it through unchanged). new Date()
  // accepts both number and string, so allow either.
  dcLastRunTs: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  dcAnalyzedCount: PropTypes.number,
  notAnalyzedCount: PropTypes.number
}

const Component = ({ tier, reviewEvidence, job, model, features, onFetch, onChange, onFilter, onCallback }) => {
  const modelAugmented = useMemo(
    () => ({ ...model, scoresFiltered: model?.scoresFiltered || [] }),
    [model, model?.scoresFiltered]
  )

  return (
    <div data-testid="stat-dc-component" className={style.component}>
      <Block
        tier={tier}
        reviewEvidence={reviewEvidence}
        job={job}
        model={modelAugmented}
        features={features}
        onFetch={onFetch}
        onChange={onChange}
        onFilter={onFilter}
        onCallback={onCallback}
      />
      {can(tier, CAPABILITY.RELEASE_QUARANTINE) ? (
        <Footer
          dcLastRunTs={model.dcLastRunTs}
          dcAnalyzedCount={model.dcAnalyzedCount}
          notAnalyzedCount={model.notAnalyzedCount}
        />
      ) : null}
    </div>
  )
}

Component.displayName = 'Component'

Component.propTypes = {
  tier: PropTypes.string,
  reviewEvidence: PropTypes.bool,
  job: PropTypes.object,
  model: PropTypes.object.isRequired,
  features: PropTypes.object.isRequired,
  onFetch: PropTypes.func,
  onChange: PropTypes.func,
  onFilter: PropTypes.func,
  onCallback: PropTypes.func
}

export default Component
